import type {
    CompRef,
    DalValue,
    NestedDataPathSegment,
    OverrideOrDefaultData,
    Pointer,
    SerializedCompStructure
} from '@wix/document-services-types'
import type {CreateExtArgs} from '@wix/document-manager-core'
import type {ComponentsExtensionAPI} from '../../components/components'
import type {InnerElementsExtensionAPI} from '../innerElements'
import type {SlotsExtensionAPI} from '../../slots/slots'
import type {ManifestApi} from '../manifest'
import {DATA_TYPES, SYSTEM_CONTAINER_TYPE} from '../../../constants/constants'
import type {DataModelExtensionAPI} from '../../dataModel/dataModel'
import {systemContainerDefaultLayout} from '../utils'

export const createSystemContainerDefinition = (layoutConfig?: any): SerializedCompStructure => ({
    componentType: SYSTEM_CONTAINER_TYPE,
    layouts: systemContainerDefaultLayout(layoutConfig)
})

export const populateSlotWithSystemContainer = (
    createExtArgs: CreateExtArgs,
    componentRef: Pointer,
    slotName: string,
    container: OverrideOrDefaultData,
    componentDefinition?: SerializedCompStructure
): void => {
    const {extensionAPI, coreConfig} = createExtArgs
    const {experimentInstance} = coreConfig
    const {components} = extensionAPI as ComponentsExtensionAPI
    const {innerElements} = extensionAPI as InnerElementsExtensionAPI
    const {slots} = extensionAPI as SlotsExtensionAPI
    const {path, nestedDataPath} = container
    const componentToAddPointer = components.getComponentToAddRef()
    let layoutConfig
    if (experimentInstance.isOpen('dm_refactorStaticSlotToDynamic')) {
        layoutConfig = container.metaData[container.metaData.containerType]?.layout
    } else {
        //specific for accordion component when exp dm_refactorStaticSlotToDynamic is off
        layoutConfig = container.metaData.data?.items?.content?.container?.simple?.layout
    }
    const definition = componentDefinition ?? createSystemContainerDefinition(layoutConfig)

    if (path) {
        innerElements.setInnerElementSlots(
            componentToAddPointer,
            componentRef as CompRef,
            path,
            slotName,
            definition,
            nestedDataPath ?? []
        )
    } else {
        slots.populate(
            componentRef as CompRef,
            slotName,
            definition,
            componentToAddPointer,
            undefined,
            nestedDataPath ?? []
        )
    }
}

export const getDataToUpdate = (
    manifest: ManifestApi,
    componentRef: Pointer,
    includeInnerElements: boolean
): OverrideOrDefaultData[] => {
    return manifest
        .getData(componentRef, includeInnerElements, [{prop: 'container'}])
        .filter(
            ({metaData}) =>
                metaData?.containerType === 'placeholder' ||
                metaData?.containerType === 'template' ||
                metaData?.containerType === 'simple'
        )
}

export const getUpdatedDataItemAtNestedPath = (
    nestedDataPath: NestedDataPathSegment[],
    updatedDataItem: Record<string, unknown>
): Record<string, any> | undefined => {
    if (!nestedDataPath?.length) {
        return updatedDataItem as Record<string, any>
    }

    let currentLevel: Record<string, any> = updatedDataItem

    for (const {key, _id, type} of nestedDataPath) {
        if (!currentLevel?.[key]) {
            return undefined
        }

        if (type === 'array') {
            const arrayItem = currentLevel[key].find((item: any) => item._id === _id)
            if (!arrayItem) {
                return undefined
            }
            currentLevel = arrayItem
        } else {
            if (currentLevel[key]._id !== _id) {
                return undefined
            }
            currentLevel = currentLevel[key]
        }
    }

    return currentLevel
}

export const getSlotInfoForComponent = (
    createExtArgs: CreateExtArgs,
    componentRef: Pointer,
    innerElementPath: string[] | undefined
) => {
    const {pointers, extensionAPI} = createExtArgs
    const {innerElements} = extensionAPI as InnerElementsExtensionAPI
    const {slots} = extensionAPI as SlotsExtensionAPI
    const {dataModel} = extensionAPI as DataModelExtensionAPI

    const pageId = pointers.structure.getPageOfComponent(componentRef as CompRef).id

    let slotDataItem: DalValue
    let slotsPointer

    if (innerElementPath) {
        slotDataItem = innerElements.getInnerElementSlotItem(componentRef as CompRef, innerElementPath)
        const innerElement = innerElements.getInnerElement(componentRef as CompRef, innerElementPath)
        slotsPointer = pointers.getPointer(innerElement!.slotsQuery, DATA_TYPES.slots)
    } else {
        slotDataItem = slots.getSlotsDataItem(componentRef as CompRef)
        slotsPointer = dataModel.components.getItemPointer(componentRef, 'slots')
    }

    return {pageId, slotDataItem, slotsPointer}
}

export const replaceExistingStaticSlotWithDynamic = (
    createExtArgs: CreateExtArgs,
    componentRef: Pointer,
    path: any,
    prop: any,
    nestedDataPath: any
) => {
    const {extensionAPI} = createExtArgs
    const {slots} = extensionAPI as SlotsExtensionAPI
    const {dataModel} = extensionAPI as DataModelExtensionAPI

    const {pageId, slotDataItem, slotsPointer} = getSlotInfoForComponent(createExtArgs, componentRef, path)

    const slottedComponentPointer = slotDataItem.slots[prop]
    const slotGuid = slots.updateSlotInData(componentRef, prop, nestedDataPath ?? [], path)
    slotDataItem.slots[slotGuid] = slottedComponentPointer
    delete slotDataItem.slots[prop]

    dataModel.addItem(slotDataItem, DATA_TYPES.slots, pageId, slotsPointer!.id)
}
